export interface SentryIgnoreHint {
    originalException?: unknown;
}

export interface SentryIgnoreEvent {
    exception?: {
        values?: Array<{
            type?: string | null;
            value?: string | null;
            stacktrace?: {
                frames?: Array<{
                    filename?: string | null;
                    function?: string | null;
                }>;
            };
        }>;
    };
    message?: string | null;
    logentry?: {message?: string | null};
    breadcrumbs?: Array<{
        category?: string | null;
        message?: string | null;
        data?: unknown;
    }>;
}

function combinedMessage(event: SentryIgnoreEvent, hint?: SentryIgnoreHint): string {
    const exceptionText = (event.exception?.values || [])
        .map((exception) => `${exception.type || ""} ${exception.value || ""}`)
        .join(" ");
    const original = hint?.originalException;
    const originalText = original instanceof Error
        ? `${original.name} ${original.message}`
        : typeof original === "string"
            ? original
            : "";
    return `${exceptionText} ${event.message || ""} ${event.logentry?.message || ""} ${originalText}`.toLowerCase();
}

function frames(event: SentryIgnoreEvent) {
    return (event.exception?.values || []).flatMap((exception) => exception.stacktrace?.frames || []);
}

function analyticsConfigEvidence(event: SentryIgnoreEvent): boolean {
    const fromFrames = frames(event).some((frame) => {
        const filename = (frame.filename || "").toLowerCase();
        const functionName = (frame.function || "").toLowerCase();
        return filename.includes("@firebase/analytics") &&
            (functionName.includes("fetchdynamicconfig") || functionName.includes("attemptfetchdynamicconfig"));
    });
    if (fromFrames) return true;

    // Production stacks are minified `/_next/static/chunks/pages/_app-*.js`
    // URLs. The SDK still logs `@firebase/analytics` on the console breadcrumb.
    return (event.breadcrumbs || []).some((crumb) => {
        const data = crumb.data == null ? "" : JSON.stringify(crumb.data);
        const text = `${crumb.category || ""} ${crumb.message || ""} ${data}`.toLowerCase();
        return text.includes("@firebase/analytics");
    });
}

/** Office / browser-extension bridge noise (Sentry JAVASCRIPT-NEXTJS-3R). */
export function shouldIgnoreOfficeAddinBridgeNoise(event: SentryIgnoreEvent, hint?: SentryIgnoreHint): boolean {
    return /object not found matching id:\d+/i.test(combinedMessage(event, hint));
}

/** Firebase Auth popup assertion on iOS Safari / in-app browsers (JAVASCRIPT-NEXTJS-21). */
export function shouldIgnoreFirebaseAuthPendingPromise(event: SentryIgnoreEvent, hint?: SentryIgnoreHint): boolean {
    return combinedMessage(event, hint).includes("pending promise was never set");
}

/**
 * Firebase Auth and Installations reject when the browser is offline or the
 * request never completes (Sentry JAVASCRIPT-NEXTJS-1ND, X5, W3, ZG, VW).
 * These are transient client conditions. Other Firebase codes stay reportable.
 */
const TRANSIENT_FIREBASE_NETWORK_CODES: readonly string[] = [
    "auth/network-request-failed",
    "installations/app-offline",
];

function isTransientFirebaseNetworkCode(code: string): boolean {
    return TRANSIENT_FIREBASE_NETWORK_CODES.includes(code);
}

function readErrorCode(error: unknown, depth = 0): string {
    if (!error || typeof error !== "object" || depth > 2) return "";
    const record = error as {code?: unknown; cause?: unknown};
    if (typeof record.code === "string" && record.code.trim()) {
        return record.code.trim().toLowerCase();
    }
    if (record.cause && record.cause !== error) {
        return readErrorCode(record.cause, depth + 1);
    }
    return "";
}

export function shouldIgnoreFirebaseTransientNetworkError(
    event: SentryIgnoreEvent,
    hint?: SentryIgnoreHint,
): boolean {
    if (isTransientFirebaseNetworkCode(readErrorCode(hint?.originalException))) {
        return true;
    }
    const message = combinedMessage(event, hint);
    return TRANSIENT_FIREBASE_NETWORK_CODES.some((code) => message.includes(code));
}

/**
 * Firebase Analytics dynamic-config fetch (JAVASCRIPT-NEXTJS-ZH).
 * A blocked or dropped request to `webConfig` rejects as an unhandled
 * TypeError. It is not an app failure.
 */
export function shouldIgnoreGenericNetworkLoadFailure(event: SentryIgnoreEvent, hint?: SentryIgnoreHint): boolean {
    const message = combinedMessage(event, hint);
    if (!message.includes("failed to fetch")) return false;
    return analyticsConfigEvidence(event);
}

/** Vidstack teardown when the media node is not a real <video> (JAVASCRIPT-NEXTJS-1CD / 1CE). */
export function shouldIgnoreVidstackDetachedMediaError(event: SentryIgnoreEvent, hint?: SentryIgnoreHint): boolean {
    const message = combinedMessage(event, hint);
    const isDetachedMedia =
        message.includes("canplaytype is not a function") ||
        message.includes("media.load is not a function") ||
        message.includes("this.media.load is not a function");
    if (!isDetachedMedia) return false;
    return frames(event).some((frame) => (frame.filename || "").toLowerCase().includes("vidstack"));
}

function eventPathname(url?: string): string {
    if (!url) return "";
    try {
        const path = new URL(url, "https://app.braiv.co").pathname.replace(/\/+$/, "");
        return path || "/";
    } catch {
        return "";
    }
}

/** Signed-out auth pages racing Firestore rules (JAVASCRIPT-NEXTJS-27). */
export function shouldIgnoreAnonymousPermissionDenied(
    event: SentryIgnoreEvent,
    hint?: SentryIgnoreHint,
    url?: string,
    userId?: string | number | null,
): boolean {
    const message = combinedMessage(event, hint);
    if (!message.includes("missing or insufficient permissions")) {
        return false;
    }
    if (userId != null && String(userId) !== "") {
        return false;
    }
    const path = eventPathname(url);
    return path === "/login" || path === "/signup";
}

/**
 * Chrome LevelDB cannot append IndexedDB logs when the device disk is full.
 * Firebase App then rejects the aborted transaction as an unhandled
 * UnknownError (Sentry JAVASCRIPT-NEXTJS-1M1). Client environment, not an
 * app bug.
 */
export function shouldIgnoreBrowserIndexedDbNoSpaceNoise(
    event: SentryIgnoreEvent,
    hint?: SentryIgnoreHint,
): boolean {
    const message = combinedMessage(event, hint);
    if (message.includes("file_error_no_space")) {
        return true;
    }
    return message.includes("indexeddb.leveldb") && message.includes("io error");
}

export function shouldIgnoreStaleChunkParseError(event: SentryIgnoreEvent, hint?: SentryIgnoreHint): boolean {
    const message = combinedMessage(event, hint);
    if (message.includes("loading css chunk") || /loading chunk \d+ failed/.test(message)) {
        return true;
    }
    if (!message.includes("unexpected eof")) {
        return false;
    }
    return frames(event).some((frame) => {
        const filename = (frame.filename || "").toLowerCase();
        return filename.includes("_next/static") || filename.includes("chunks/");
    });
}
